import { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import MypageSidebar from '../../MypageSidebar';
import ReviewForm from './ReviewForm';
import ReviewStar from './ReviewStar';
import api from '../../../../api/api';
import { updateReview } from '../../../../api/reservation';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LocationState, ReviewDataProps } from '@/types/mypage/ReviewType';
import { ModalOneBtn, ModalTwoBtns } from '../../../../components/modals';
import useGetReview from '../../../../hooks/reservation/review/useGetReview';
import LoadingSpinner from '../../../../components/LoadingSpinner';

const ReviewDetail = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState<string[]>([]);
  const [rating, setRating] = useState<number>(0);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [showInvalidAlert, setShowInvalidAlert] = useState(false);
  const [review, setReview] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const { id: classId } = useParams<{ id: string }>();
  const selectedMenu = new URLSearchParams(location.search).get('menu') || 'myreview';
  const locationState = (location.state as LocationState) || {};
  const editReviewId = locationState.editReviewId;
  const isEditMode = editReviewId != null;
  const className = locationState.className || '';

  const { data: existingReview, isLoading: isLoadingEdit } = useGetReview(
    classId,
    isEditMode ? String(editReviewId) : undefined
  );

  useEffect(() => {
    if (!isEditMode || !existingReview) return;
    setTitle(existingReview.title);
    setReview(existingReview.content);
    setRating(existingReview.rating ?? 0);
    setSelectedImage(
      existingReview.reviewImages?.length ? [...existingReview.reviewImages] : []
    );
  }, [isEditMode, existingReview]);

  const handleMenuClick = (menuKey: string) => {
    navigate(`/mypage?menu=${menuKey}`);
  };

  const createReview = async (reviewData: ReviewDataProps) => {
    const token = localStorage.getItem('token');

    const response = await api.post(
      `/dance-classes/${classId}/reviews`,
      {
        ...reviewData,
        reviewImages: reviewData.reviewImages
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  };

  const mutation = useMutation({
    mutationFn: async (reviewData: ReviewDataProps) => {
      if (isEditMode && classId) {
        return updateReview(classId, String(editReviewId), reviewData);
      }
      return createReview(reviewData);
    },
    onSuccess: async () => {
      if (classId) {
        await queryClient.invalidateQueries({ queryKey: ['reviews', classId] });
        if (isEditMode && editReviewId != null) {
          await queryClient.invalidateQueries({
            queryKey: ['review', classId, String(editReviewId)]
          });
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['userreviews'] });
      await queryClient.invalidateQueries({ queryKey: ['userReviewClassIds'] });
      navigate(`/classes/${classId}?tab=reviews`);
    },
    onError: (error: unknown) => {
      console.error(error);
    }
  });

  const handleReview = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setReview(e.target.value);
  };

  const handleTitle = (e: ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !review.trim()) {
      setShowInvalidAlert(true);
      return;
    }

    const reviewData: ReviewDataProps = {
      title,
      content: review,
      rating,
      reviewImages: selectedImage
    };

    mutation.mutate(reviewData);
  };

  const handleClickCancel = () => setShowAlert(true);
  const hideClickCancel = () => setShowAlert(false);

  if (isEditMode && isLoadingEdit) {
    return (
      <Container>
        <MypageSidebar selectedMenu={selectedMenu} onMenuClick={handleMenuClick} />
        <ReviewContainer>
          <LoadingSpinner isLoading={true} marginTop="120px" />
        </ReviewContainer>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <MypageSidebar selectedMenu={selectedMenu} onMenuClick={handleMenuClick} />

        <ReviewContainer>
          <ClassTitle>{className}</ClassTitle>
          <Title>{isEditMode ? '리뷰 수정' : '리뷰 작성'}</Title>
          <Notice>
            <li>* 제목은 최대 50자까지 입력 가능합니다.</li>
            <li>* 내용은 최대 1000자까지 입력 가능합니다.</li>
          </Notice>

          <ReviewForm
            title={title}
            review={review}
            handleTitle={handleTitle}
            handleReview={handleReview}
            selectedImages={selectedImage}
            setSelectedImages={setSelectedImage}
          />
          <ReviewStar rating={rating} setRating={setRating} />

          <FinalSection>
            <Buttons>
              <CancelButton onClick={handleClickCancel}>
                취소
                {showAlert && (
                  <ModalTwoBtns
                    message={
                      <span>
                        <span>
                          해당 페이지를 벗어나면 <br />
                        </span>
                        <span>
                          작성 중인 글이 <ColoredText> 모두 삭제 </ColoredText> 됩니다 <br />
                        </span>
                        <span>떠나시겠습니까?</span>
                      </span>
                    }
                    onClose={hideClickCancel}
                    showButtons={true}
                    primaryLabel="남기"
                    secondaryLabel="떠나기"
                    onPrimaryClick={hideClickCancel}
                    onSecondaryClick={() => navigate('/mypage')}
                  />
                )}
              </CancelButton>

              <SubmitButton onClick={handleSubmit}>{isEditMode ? '수정' : '작성'}</SubmitButton>
              {showInvalidAlert && (
                <ModalOneBtn
                  message={
                    <AlertText>
                      모든 항목을 {'\n'}
                      <ColoredText>적절하게 </ColoredText>
                      입력했는지 확인해주세요.
                    </AlertText>
                  }
                  onClose={() => setShowInvalidAlert(false)}
                  showButtons={true}
                />
              )}
            </Buttons>
          </FinalSection>
        </ReviewContainer>
      </Container>
    </>
  );
};

export default ReviewDetail;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  background-color: black;
  width: 1440px;

  @media (max-width: 600px) {
    width: 100%;
    flex-direction: column;
    align-items: center;
  }
`;

const ReviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: black;
  color: #fff;
  margin-top: 38px;
  width: 880px;
  margin-left: 50px;

  @media (max-width: 600px) {
    width: 100%;
    margin-left: 0;
    padding: 0 16px;
  }
`;

const ClassTitle = styled.div`
  color: #fff;
  font-size: 30px;
  font-weight: 600;
  margin-bottom: 23px;
  margin-left: 200px;

  @media (max-width: 600px) {
    margin-left: 0;
    text-align: center;
    font-size: 22px;
  }
`;

const Title = styled.div`
  font-weight: 600;
  color: #fff;
  font-size: 22px;
  margin-left: 55px;

  @media (max-width: 600px) {
    display: none;
  }
`;

const Notice = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  color: #b2b2b2;
  font-size: 14px;
  list-style: none;
  margin-bottom: 10px;
  margin-right: 220px;

  @media (max-width: 600px) {
    margin-right: 0;
    font-size: 12px;
    width: 100%;
  }
`;

const FinalSection = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 244px;
  gap: 24px;
  margin-top: 75px;
  margin-left: 519px;

  @media (max-width: 600px) {
    margin-left: 0;
    justify-content: flex-end;
    margin-bottom: 100px;
    width: 100%;
  }
`;

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
`;

const CancelButton = styled.button`
  width: 64px;
  height: 36px;
  background-color: transparent;
  color: #fff;
  border: 2px solid #9819c3;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    background-color: #9819c3;
  }
`;

const SubmitButton = styled.button`
  width: 64px;
  height: 36px;
  background-color: #9819c3;
  color: #fff;
  border: 2px solid #9819c3;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
`;

const ColoredText = styled.span`
  color: #a60f62;
`;

const AlertText = styled.span`
  text-align: center;
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 21px;
  white-space: pre-line;

  ${({ theme }) => theme.media.tablet} {
    font-size: 16px;
  }
`;
